import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { format, addDays, nextMonday } from "date-fns";
import { ArrowLeft, ArrowRight, Sparkles, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import DateField from "../components/onboarding/DateField";
import RaceManager from "../components/onboarding/RaceManager";
import MetricsSection, { metricsValid } from "../components/onboarding/MetricsSection";
import GarminIntro from "../components/onboarding/GarminIntro";
import { CoachLine, Chip, FieldLabel } from "../components/onboarding/parts";

const SECTIONS = ["connect", "sport", "level", "goal", "races", "metrics", "physio", "availability", "life", "start", "review"];
const REVIEW_IDX = SECTIONS.indexOf("review");

const SPORTS = ["Running", "Cycling", "Swimming", "Triathlon", "Strength / Gym", "Hybrid / Multiple"];
const DISCIPLINE_OPTS = [["running", "Running"], ["cycling", "Cycling"], ["swimming", "Swimming"], ["strength", "Strength"]];
const LEVELS = ["Just starting out", "Recreational", "Intermediate", "Advanced / Competitive"];
const GOALS = ["Finish my first event", "Hit a target time", "Get fitter & healthier", "Return after a break", "Stay consistent", "Train without injury"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = [["morning", "Morning"], ["midday", "Midday"], ["evening", "Evening"], ["flexible", "Flexible"]];
const ACCESS = [["gym", "Gym"], ["pool", "Pool"], ["treadmill", "Treadmill"], ["indoor_trainer", "Indoor trainer"], ["track", "Track"]];

const isMulti = (s) => s === "Triathlon" || s === "Hybrid / Multiple";
const isAdvanced = (l) => ["Intermediate", "Advanced / Competitive"].includes(l);

function disciplinesOf(a) {
  const s = a.primary_sport;
  if (s === "Triathlon") return a.disciplines?.length ? a.disciplines : ["swimming", "cycling", "running"];
  if (s === "Hybrid / Multiple") return a.disciplines?.length ? a.disciplines : ["running"];
  const map = { Running: "running", Cycling: "cycling", Swimming: "swimming", "Strength / Gym": "strength" };
  return [map[s] || "running"];
}

function computeStartDate(opt, custom) {
  const t = new Date();
  if (opt === "today") return format(t, "yyyy-MM-dd");
  if (opt === "tomorrow") return format(addDays(t, 1), "yyyy-MM-dd");
  if (opt === "next_monday") return format(nextMonday(t), "yyyy-MM-dd");
  return custom || "";
}

export default function Onboarding() {
  const [a, setA] = useState({
    disciplines: [], races: [], metrics: {}, physio: {}, preferred_days: [],
    access: { gym: true, treadmill: true }, sleep_quality_typical: "ok", session_minutes: 60,
  });
  const [idx, setIdx] = useState(0);
  const [returnToReview, setReturnToReview] = useState(false);
  const [understanding, setUnderstanding] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const lastPreviewSig = useRef("");
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState("");
  const { user, setOnboarded } = useAuth();
  const navigate = useNavigate();

  const key = SECTIONS[idx];
  const progress = Math.round((idx / SECTIONS.length) * 100);
  const set = (patch) => setA((p) => ({ ...p, ...patch }));
  const disciplines = useMemo(() => disciplinesOf(a), [a]);

  const applyAutofill = (af = {}) => {
    setA((p) => {
      const n = { ...p, garmin: af, garmin_connected: true, physio: { ...p.physio } };
      if (af.age) n.physio.age = af.age;
      if (af.weight_kg) n.physio.weight_kg = af.weight_kg;
      if (af.sex) n.physio.sex = af.sex;
      if (af.resting_hr) n.physio.resting_hr = af.resting_hr;
      if (af.primary_sport && SPORTS.includes(af.primary_sport)) n.primary_sport = af.primary_sport;
      if (af.weekly_summary) n.current_training = af.weekly_summary;
      const dMap = { Running: "running", Cycling: "cycling", Swimming: "swimming" };
      const d = dMap[af.primary_sport];
      if (d) {
        const m = { ...(p.metrics?.[d] || {}) };
        if (d === "running" && af.threshold_pace) m.threshold = `${af.threshold_pace}/km`;
        else if (d === "cycling" && af.threshold_hr) m.threshold = `${af.threshold_hr} bpm threshold`;
        if (af.vo2max_running || af.vo2max_cycling) m.recent = m.recent || `VO₂max ${af.vo2max_running || af.vo2max_cycling}`;
        n.metrics = { ...p.metrics, [d]: m };
      }
      return n;
    });
    setIdx(1);
  };

  const buildProfile = () => {
    const start_date = computeStartDate(a.start_option, a.start_date);
    const goalLabel = GOALS.find((g) => g === a.goal_type) || a.goal_type;
    return {
      name: user?.name,
      start_option: a.start_option, start_date,
      primary_sport: a.primary_sport, disciplines,
      experience_level: a.experience_level, years_training: a.years_training || null,
      goal_type: a.goal_type, goal_text: a.goal_text || goalLabel,
      races: a.races, metrics: a.metrics, physio: a.physio,
      days_per_week: a.days_per_week, preferred_days: a.preferred_days,
      session_minutes: a.session_minutes, preferred_time_of_day: a.preferred_time_of_day,
      access: a.access,
      current_training: a.current_training || "unknown",
      other_activities: a.other_activities || "none",
      constraints: a.constraints || "none",
      sleep_quality_typical: a.sleep_quality_typical,
      limitations: a.limitations || "none reported",
      garmin: a.garmin || null,
    };
  };

  // fetch Caden's first read whenever the review step is shown
  useEffect(() => {
    if (key !== "review") return;
    const profile = buildProfile();
    const sig = JSON.stringify(profile);
    if (sig === lastPreviewSig.current) return; // nothing changed since last read
    lastPreviewSig.current = sig;
    let cancelled = false;
    setPreviewLoading(true);
    api.post("/onboarding/preview", { profile })
      .then((r) => { if (!cancelled) setUnderstanding(r.data.understanding || ""); })
      .catch(() => { if (!cancelled) setUnderstanding(""); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const canContinue = (() => {
    switch (key) {
      case "connect": return true;
      case "sport": return !!a.primary_sport && (!isMulti(a.primary_sport) || (a.disciplines?.length > 0));
      case "level": return !!a.experience_level;
      case "goal": return !!a.goal_type;
      case "races": return true;
      case "metrics": return metricsValid(a.experience_level, disciplines, a.metrics);
      case "physio": return true;
      case "availability": return !!a.days_per_week && !!a.session_minutes && !!a.preferred_time_of_day;
      case "life": return true;
      case "start": return !!a.start_option && (a.start_option !== "custom" || !!a.start_date);
      case "review": return true;
      default: return true;
    }
  })();

  const next = () => {
    if (returnToReview && key !== "review") { setReturnToReview(false); setIdx(REVIEW_IDX); return; }
    if (idx + 1 >= SECTIONS.length) build();
    else setIdx(idx + 1);
  };
  const back = () => idx > 0 && setIdx(idx - 1);
  const jumpTo = (k) => { setReturnToReview(true); setIdx(SECTIONS.indexOf(k)); };

  const toggleArr = (field, val) => {
    const cur = a[field] || [];
    set({ [field]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] });
  };

  const build = async () => {
    setBuilding(true);
    setError("");
    try {
      await api.post("/onboarding/complete", { profile: buildProfile() });
      setOnboarded(true);
      navigate("/today");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBuilding(false);
    }
  };

  if (building) {
    return (
      <div className="min-h-screen caden-grain bg-background flex items-center justify-center p-6">
        <div className="relative z-10 max-w-lg w-full text-center">
          <div className="brand-gradient w-16 h-16 rounded-2xl mx-auto flex items-center justify-center">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: "linear" }}>
              <Sparkles className="text-white" size={30} />
            </motion.div>
          </div>
          <h2 className="font-heading font-extrabold text-2xl mt-6 text-foreground">Building your plan…</h2>
          <p className="text-muted-foreground mt-2">Shaping your first week around your goal, races and real life.</p>
          {error && <p className="text-destructive mt-4" data-testid="onboarding-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen caden-grain bg-background flex flex-col">
      <div className="relative z-10 px-5 pt-6 flex items-center justify-between">
        <Logo size={32} />
        <span className="text-sm text-muted-foreground" data-testid="onboarding-progress">{idx + 1} / {SECTIONS.length}</span>
      </div>
      <div className="relative z-10 h-1.5 bg-muted mx-5 mt-4 rounded-full overflow-hidden">
        <motion.div className="h-full brand-gradient" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
      </div>

      <div className="relative z-10 flex-1 max-w-xl w-full mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div key={key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.25 }}>

            {key === "connect" && (
              <GarminIntro onConnected={applyAutofill} onSkip={() => setIdx(1)} />
            )}

            {key === "sport" && (
              <>
                <CoachLine>Hey, I'm Caden — your coach. What's your main sport?</CoachLine>
                <div className="grid grid-cols-2 gap-3" data-testid="sport-options">
                  {SPORTS.map((s) => <Chip key={s} testid={`sport-${s}`} active={a.primary_sport === s} onClick={() => set({ primary_sport: s, disciplines: s === "Triathlon" ? ["swimming", "cycling", "running"] : [] })}>{s}</Chip>)}
                </div>
                {isMulti(a.primary_sport) && (
                  <div className="mt-5">
                    <FieldLabel>Which disciplines should I plan for?</FieldLabel>
                    <div className="flex flex-wrap gap-2" data-testid="discipline-options">
                      {DISCIPLINE_OPTS.map(([k, l]) => (
                        <button key={k} data-testid={`discipline-${k}`} onClick={() => toggleArr("disciplines", k)}
                          className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${a.disciplines?.includes(k) ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/50"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {key === "level" && (
              <>
                <CoachLine subtitle={a.experience_level === "Advanced / Competitive" ? "Seasoned, ha? Then I'll dig a little deeper — a few extra questions, worth it." : "Be honest — there's no wrong answer."}>How would you describe your level?</CoachLine>
                <div className="grid grid-cols-2 gap-3" data-testid="level-options">
                  {LEVELS.map((l) => <Chip key={l} testid={`level-${l}`} active={a.experience_level === l} onClick={() => set({ experience_level: l })}>{l}</Chip>)}
                </div>
                {isAdvanced(a.experience_level) && (
                  <div className="mt-5">
                    <FieldLabel>How many years have you trained this sport?</FieldLabel>
                    <Input data-testid="years-input" type="number" value={a.years_training || ""} onChange={(e) => set({ years_training: e.target.value })} placeholder="e.g. 4" className="h-11" />
                  </div>
                )}
              </>
            )}

            {key === "goal" && (
              <>
                <CoachLine>What are we really working towards?</CoachLine>
                <div className="grid grid-cols-2 gap-3" data-testid="goal-options">
                  {GOALS.map((g) => <Chip key={g} testid={`goal-${g}`} active={a.goal_type === g} onClick={() => set({ goal_type: g })}>{g}</Chip>)}
                </div>
                {a.goal_type && (
                  <div className="mt-5">
                    <FieldLabel>In one sentence — what does success look like? <span className="text-muted-foreground font-normal">(optional)</span></FieldLabel>
                    <Input data-testid="goal-text" value={a.goal_text || ""} onChange={(e) => set({ goal_text: e.target.value })} placeholder="e.g. sub 1:40 half, feeling strong" className="h-11" />
                  </div>
                )}
              </>
            )}

            {key === "races" && (
              <>
                <CoachLine subtitle="Add each race with its date and priority. A = main goal, B = important, C = tune-up. No race? Just continue.">Any races on the calendar?</CoachLine>
                <RaceManager sport={a.primary_sport} value={a.races} onChange={(races) => set({ races })} />
              </>
            )}

            {key === "metrics" && (
              <>
                <CoachLine subtitle={isAdvanced(a.experience_level) ? "This is where precision pays off — give me a threshold or a recent result per discipline, or tell me you're not sure." : "Rough answers are perfect — I'll refine everything from your real sessions."}>Let's calibrate your training.</CoachLine>
                <MetricsSection disciplines={disciplines} level={a.experience_level} value={a.metrics} onChange={(metrics) => set({ metrics })} />
              </>
            )}

            {key === "physio" && (
              <>
                <CoachLine subtitle="All optional — helps me pace and keep you safe. Skip anything you'd rather not share.">A few basics about you.</CoachLine>
                <div className="grid grid-cols-2 gap-4">
                  <div><FieldLabel>Age</FieldLabel><Input data-testid="physio-age" type="number" value={a.physio.age || ""} onChange={(e) => set({ physio: { ...a.physio, age: e.target.value } })} placeholder="e.g. 32" className="h-11" /></div>
                  <div><FieldLabel>Weight (kg)</FieldLabel><Input data-testid="physio-weight" type="number" value={a.physio.weight_kg || ""} onChange={(e) => set({ physio: { ...a.physio, weight_kg: e.target.value } })} placeholder="e.g. 72" className="h-11" /></div>
                  <div className="col-span-2">
                    <FieldLabel>Sex</FieldLabel>
                    <div className="flex gap-2" data-testid="physio-sex">
                      {["Male", "Female", "Other"].map((s) => (
                        <button key={s} data-testid={`physio-sex-${s}`} onClick={() => set({ physio: { ...a.physio, sex: s } })}
                          className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${a.physio.sex === s ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/50"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2"><FieldLabel>Resting heart rate <span className="text-muted-foreground font-normal">(optional)</span></FieldLabel><Input data-testid="physio-rhr" type="number" value={a.physio.resting_hr || ""} onChange={(e) => set({ physio: { ...a.physio, resting_hr: e.target.value } })} placeholder="e.g. 52" className="h-11" /></div>
                </div>
              </>
            )}

            {key === "availability" && (
              <>
                <CoachLine subtitle="Think about your actual life, not your best week.">When can you realistically train?</CoachLine>
                <FieldLabel>Days per week</FieldLabel>
                <div className="flex flex-wrap gap-2 mb-5" data-testid="days-per-week">
                  {["2", "3", "4", "5", "6", "7"].map((d) => (
                    <button key={d} data-testid={`days-${d}`} onClick={() => set({ days_per_week: d })}
                      className={`w-11 h-11 rounded-xl border font-semibold transition-colors ${a.days_per_week === d ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/50"}`}>{d}</button>
                  ))}
                </div>
                <FieldLabel>Preferred days <span className="text-muted-foreground font-normal">(optional)</span></FieldLabel>
                <div className="flex flex-wrap gap-2 mb-5" data-testid="preferred-days">
                  {DAYS.map((d) => (
                    <button key={d} data-testid={`day-${d}`} onClick={() => toggleArr("preferred_days", d)}
                      className={`px-3 py-2 rounded-full border text-sm font-medium transition-colors ${a.preferred_days?.includes(d) ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/50"}`}>{d}</button>
                  ))}
                </div>
                <FieldLabel>Typical session length</FieldLabel>
                <div className="flex flex-wrap gap-2 mb-5" data-testid="session-minutes">
                  {[30, 45, 60, 90, 120].map((m) => (
                    <button key={m} data-testid={`minutes-${m}`} onClick={() => set({ session_minutes: m })}
                      className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors ${a.session_minutes === m ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/50"}`}>{m === 120 ? "120+ min" : `${m} min`}</button>
                  ))}
                </div>
                <FieldLabel>Preferred time of day</FieldLabel>
                <div className="flex flex-wrap gap-2 mb-5" data-testid="time-of-day">
                  {TIMES.map(([k, l]) => (
                    <button key={k} data-testid={`time-${k}`} onClick={() => set({ preferred_time_of_day: k })}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${a.preferred_time_of_day === k ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/50"}`}>{l}</button>
                  ))}
                </div>
                <FieldLabel>What do you have access to?</FieldLabel>
                <div className="space-y-2" data-testid="access-toggles">
                  {ACCESS.map(([k, l]) => (
                    <div key={k} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                      <span className="text-foreground text-sm font-medium">{l}</span>
                      <Switch data-testid={`access-${k}`} checked={!!a.access[k]} onCheckedChange={(v) => set({ access: { ...a.access, [k]: v } })} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {key === "life" && (
              <>
                <CoachLine subtitle="The real stuff — this is what makes your plan actually fit.">Your life & body.</CoachLine>
                <div className="space-y-5">
                  <div>
                    <FieldLabel>{isAdvanced(a.experience_level) ? "Your current training week (volume + key sessions)" : "How active are you right now?"}</FieldLabel>
                    <Textarea data-testid="current-training" value={a.current_training || ""} onChange={(e) => set({ current_training: e.target.value })} rows={2} placeholder={isAdvanced(a.experience_level) ? "e.g. ~40km/week, one interval + one long run" : "e.g. I walk daily, run occasionally"} />
                  </div>
                  <div>
                    <FieldLabel>Other sports / activities <span className="text-muted-foreground font-normal">(optional)</span></FieldLabel>
                    <Input data-testid="other-activities" value={a.other_activities || ""} onChange={(e) => set({ other_activities: e.target.value })} placeholder="e.g. football Tuesdays, gym 2x" className="h-11" />
                  </div>
                  <div>
                    <FieldLabel>Anything I should plan around? <span className="text-muted-foreground font-normal">(work, family, travel)</span></FieldLabel>
                    <Textarea data-testid="constraints" value={a.constraints || ""} onChange={(e) => set({ constraints: e.target.value })} rows={2} placeholder="e.g. busy Fridays, travel monthly" />
                  </div>
                  <div>
                    <FieldLabel>How's your sleep, typically?</FieldLabel>
                    <div className="flex gap-2" data-testid="sleep-typical">
                      {["poor", "ok", "good"].map((s) => (
                        <button key={s} data-testid={`sleep-${s}`} onClick={() => set({ sleep_quality_typical: s })}
                          className={`px-5 py-2 rounded-full border text-sm font-medium capitalize transition-colors ${a.sleep_quality_typical === s ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/50"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Any injuries, pain or health things I should know? <span className="text-muted-foreground font-normal">(safety first)</span></FieldLabel>
                    <Textarea data-testid="limitations" value={a.limitations || ""} onChange={(e) => set({ limitations: e.target.value })} rows={2} placeholder="e.g. sensitive left knee — or leave empty" />
                  </div>
                </div>
              </>
            )}

            {key === "start" && (
              <>
                <CoachLine subtitle="Many athletes like a clean start on Monday. Your call.">One last thing — when do we begin?</CoachLine>
                <div className="grid grid-cols-2 gap-3" data-testid="start-options">
                  {[["today", "Today"], ["tomorrow", "Tomorrow"], ["next_monday", "Next Monday"], ["custom", "Pick a date"]].map(([k, l]) => (
                    <Chip key={k} testid={`start-${k}`} active={a.start_option === k} onClick={() => set({ start_option: k })}>{l}</Chip>
                  ))}
                </div>
                {a.start_option === "custom" && (
                  <div className="mt-4"><DateField testid="start-date-picker" value={a.start_date} onChange={(v) => set({ start_date: v })} placeholder="Choose your start date" /></div>
                )}
              </>
            )}

            {key === "review" && (
              <Review a={a} understanding={understanding} loading={previewLoading} disciplines={disciplines} onJump={jumpTo} set={set} />
            )}

          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10">
          {idx > 0 ? (
            <button data-testid="onboarding-back" onClick={back} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Back</button>
          ) : <span />}
          {key !== "connect" && (
            <Button data-testid="onboarding-continue" onClick={next} disabled={!canContinue} className="h-11 px-6 rounded-xl font-semibold active:scale-95 transition-transform">
              {key === "review" ? "Build my plan" : returnToReview ? "Save & review" : "Continue"} <ArrowRight size={18} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function EditRow({ label, value, onEdit, testid }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-foreground font-medium truncate" data-testid={testid}>{value || "—"}</p>
      </div>
      {onEdit && (
        <button onClick={onEdit} data-testid={`${testid}-edit`} className="flex items-center gap-1 text-xs text-primary font-semibold shrink-0 ml-3 hover:underline">
          <Pencil size={12} /> Change
        </button>
      )}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{title}</p>
      {children}
    </div>
  );
}

function Review({ a, understanding, loading, disciplines, onJump, set }) {
  const startDate = computeStartDate(a.start_option, a.start_date);
  const metricsSummary = disciplines.map((d) => {
    const m = a.metrics[d] || {};
    if (m.not_sure) return `${d}: not sure (estimate)`;
    const v = m.threshold || m.recent || m.capacity || m.note || "guided";
    return `${d}: ${v}`;
  }).join(" · ");

  return (
    <div className="space-y-4" data-testid="review-section">
      <CoachLine>Here's what I've got. Fix anything that's off, then I'll build your plan.</CoachLine>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="text-brand-orange" size={16} />
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Caden's first read</p>
        </div>
        {loading ? (
          <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-11/12" /><Skeleton className="h-4 w-3/4" /></div>
        ) : (
          <p className="text-foreground leading-relaxed" data-testid="review-understanding">{understanding || "I've got your details — let's build your plan."}</p>
        )}
      </div>

      <Group title="Goal & start">
        <EditRow label="Start" value={startDate ? `${a.start_option?.replace("_", " ")} (${startDate})` : "—"} onEdit={() => onJump("start")} testid="review-start" />
        <EditRow label="Goal" value={a.goal_type} onEdit={() => onJump("goal")} testid="review-goal" />
        <div className="pt-2">
          <p className="text-xs text-muted-foreground mb-1">Goal in your words</p>
          <Input data-testid="review-goal-text" value={a.goal_text || ""} onChange={(e) => set({ goal_text: e.target.value })} placeholder="optional" className="h-10" />
        </div>
      </Group>

      <Group title="Profile">
        <EditRow label="Sport" value={a.primary_sport} onEdit={() => onJump("sport")} testid="review-sport" />
        <EditRow label="Level" value={a.experience_level} onEdit={() => onJump("level")} testid="review-level" />
      </Group>

      <Group title="Races">
        {a.races?.length ? (
          <div className="space-y-1" data-testid="review-races">
            {a.races.map((r) => <p key={r.id} className="text-foreground text-sm">[{r.priority}] {r.name} — {r.race_type} · {r.date}</p>)}
          </div>
        ) : <p className="text-muted-foreground text-sm" data-testid="review-races">No races added</p>}
        <button onClick={() => onJump("races")} data-testid="review-races-edit" className="flex items-center gap-1 text-xs text-primary font-semibold mt-2 hover:underline"><Pencil size={12} /> Edit races</button>
      </Group>

      <Group title="Calibration">
        <p className="text-foreground text-sm capitalize" data-testid="review-metrics">{metricsSummary || "guided"}</p>
        <button onClick={() => onJump("metrics")} data-testid="review-metrics-edit" className="flex items-center gap-1 text-xs text-primary font-semibold mt-2 hover:underline"><Pencil size={12} /> Edit calibration</button>
      </Group>

      <Group title="Availability">
        <EditRow label="Days / week" value={a.days_per_week} onEdit={() => onJump("availability")} testid="review-days" />
        <EditRow label="Session length" value={a.session_minutes ? `${a.session_minutes} min` : "—"} onEdit={() => onJump("availability")} testid="review-minutes" />
        <EditRow label="Preferred time" value={a.preferred_time_of_day} onEdit={() => onJump("availability")} testid="review-time" />
        <EditRow label="Equipment" value={Object.keys(a.access || {}).filter((k) => a.access[k]).join(", ") || "—"} onEdit={() => onJump("availability")} testid="review-access" />
      </Group>

      <Group title="Body (optional)">
        <div className="grid grid-cols-2 gap-3">
          <div><p className="text-xs text-muted-foreground mb-1">Age</p><Input data-testid="review-age" type="number" value={a.physio?.age || ""} onChange={(e) => set({ physio: { ...a.physio, age: e.target.value } })} className="h-10" /></div>
          <div><p className="text-xs text-muted-foreground mb-1">Weight (kg)</p><Input data-testid="review-weight" type="number" value={a.physio?.weight_kg || ""} onChange={(e) => set({ physio: { ...a.physio, weight_kg: e.target.value } })} className="h-10" /></div>
        </div>
      </Group>

      <Group title="Life & safety">
        <div className="space-y-3">
          <div><p className="text-xs text-muted-foreground mb-1">Current training</p><Textarea data-testid="review-current" value={a.current_training || ""} onChange={(e) => set({ current_training: e.target.value })} rows={2} /></div>
          <div><p className="text-xs text-muted-foreground mb-1">Constraints</p><Input data-testid="review-constraints" value={a.constraints || ""} onChange={(e) => set({ constraints: e.target.value })} className="h-10" /></div>
          <EditRow label="Sleep" value={a.sleep_quality_typical} onEdit={() => onJump("life")} testid="review-sleep" />
          <div><p className="text-xs text-muted-foreground mb-1">Injuries / health</p><Input data-testid="review-limitations" value={a.limitations || ""} onChange={(e) => set({ limitations: e.target.value })} placeholder="none" className="h-10" /></div>
        </div>
      </Group>
    </div>
  );
}
